import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Translate, PencilSimple, FloppyDisk, X, Sparkle, ArrowCounterClockwise } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const FIELD_LABELS = {
  name: "Name",
  location: "Location",
  board: "Board",
  management: "Management",
  description: "About",
  enriched_article: "Full description",
};
const ORDER = ["name", "location", "board", "management", "description", "enriched_article"];

export default function InstitutionLocalization({ inst, canManage }) {
  const id = inst.id;
  const [info, setInfo] = useState(null); // {available, lang, lang_label, fields, source, edited}
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/institutions/${id}/localization`).then((r) => setInfo(r.data)).catch(() => setInfo({ available: false }));
  }, [id]);

  if (!info || !info.available) return null;

  const langLabel = info.lang_label || info.lang;
  const fields = info.fields;

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/institutions/${id}/localize`);
      setInfo((p) => ({ ...p, fields: data.fields, source: data.source, edited: data.edited }));
      toast.success(`${info.lang} version ready`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not generate translation");
    } finally { setGenerating(false); }
  };

  const startEdit = () => { setDraft({ ...fields }); setEditing(true); };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/institutions/${id}/localization`, { fields: draft });
      setInfo((p) => ({ ...p, fields: data.fields, source: data.source, edited: data.edited }));
      setEditing(false);
      toast.success(`${info.lang} version updated`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  const present = ORDER.filter((k) => (editing ? k in draft || fields?.[k] : fields?.[k]));

  return (
    <Card className="mt-6 border-secondary/30 bg-secondary/[0.03] p-6 shadow-sm" id="localized-section" data-testid="localized-section">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-heading text-xl font-medium">
          <Translate className="text-secondary" size={22} weight="duotone" />
          <span data-testid="localized-lang-label">{langLabel}</span>
          <span className="text-sm font-normal text-muted-foreground">· {info.lang}</span>
        </h2>
        {fields && canManage && !editing && (
          <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={startEdit} data-testid="localized-edit-btn">
            <PencilSimple size={15} /> Edit
          </Button>
        )}
      </div>

      {!fields ? (
        <div className="flex flex-col items-start gap-3 rounded-xl border border-dashed border-secondary/40 bg-white/60 p-5">
          <p className="text-sm text-muted-foreground">
            Read this institution's page in {info.lang} ({langLabel}). We'll create it with AI — you can refine it afterwards.
          </p>
          <Button onClick={generate} disabled={generating} className="rounded-full gap-1.5" data-testid="localized-generate-btn">
            <Sparkle size={15} weight="fill" /> {generating ? "Translating…" : `View in ${info.lang}`}
          </Button>
        </div>
      ) : editing ? (
        <div className="space-y-4">
          {present.map((k) => (
            <div key={k} className="space-y-1.5">
              <Label>{FIELD_LABELS[k] || k}</Label>
              {k === "name" || k === "location" || k === "board" || k === "management" ? (
                <Input value={draft[k] || ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} data-testid={`localized-input-${k}`} />
              ) : (
                <Textarea rows={k === "enriched_article" ? 6 : 3} value={draft[k] || ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} data-testid={`localized-input-${k}`} />
              )}
            </div>
          ))}
          <div className="flex items-center gap-2">
            <Button onClick={save} disabled={saving} className="rounded-full gap-1.5" data-testid="localized-save-btn">
              <FloppyDisk size={15} /> {saving ? "Saving…" : "Save"}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)} className="rounded-full gap-1.5" data-testid="localized-cancel-btn">
              <X size={15} /> Cancel
            </Button>
            <button type="button" onClick={generate} disabled={generating} className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="localized-regen-btn">
              <ArrowCounterClockwise size={13} /> {generating ? "Re-translating…" : "Re-translate with AI"}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4" lang={info.lang} data-testid="localized-content">
          {fields.name && <h3 className="font-heading text-2xl font-medium" data-testid="localized-name">{fields.name}</h3>}
          <div className="flex flex-wrap gap-2 text-sm">
            {fields.location && <span className="rounded-full bg-white px-3 py-1 text-foreground/80 shadow-sm">{fields.location}</span>}
            {fields.board && <span className="rounded-full bg-white px-3 py-1 text-foreground/80 shadow-sm">{fields.board}</span>}
            {fields.management && <span className="rounded-full bg-white px-3 py-1 text-foreground/80 shadow-sm">{fields.management}</span>}
          </div>
          {fields.description && <p className="max-w-2xl leading-relaxed text-foreground/90">{fields.description}</p>}
          {fields.enriched_article && fields.enriched_article !== fields.description && (
            <div className="max-w-2xl space-y-2">
              {fields.enriched_article.split("\n").filter(Boolean).map((p, i) => (
                <p key={i} className="text-sm leading-relaxed text-foreground/90">{p}</p>
              ))}
            </div>
          )}
          <p className="pt-1 text-xs text-muted-foreground">
            {info.source === "manual" ? "Reviewed & edited by the institution" : "AI-assisted translation"} · you can suggest improvements
          </p>
        </div>
      )}
    </Card>
  );
}
